import {Injectable} from '@angular/core';
import {RealEstate, RealEstateCards} from "../../interfaces/real-estate";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {EnvironmentConfigService} from '../../core/config/environment-config.service';
import {AuthenticationService} from '../authentication/authentication.service';
import {firstValueFrom, Observable, of} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RealEstateService {

  headers = new HttpHeaders().set('content-type', 'application/json')

  constructor(
    private httpClient: HttpClient,
    private environmentConfig: EnvironmentConfigService,
    private authenticationService: AuthenticationService) {
  }

  private realEstatesTree: { [key: string]: { [key: string]: RealEstateCards[] | null } | undefined } = {};
  private statesToCitiesCache: { [key: string]: { 'city': string, 'file': string }[] } | null = null;
  private statesLoadPromise: Promise<void> | null = null;
  private realEstateCache: Map<string, RealEstate> = new Map<string, RealEstate>();

  // Real estate types in Portuguese
  private readonly REAL_ESTATE_TYPES = [
    'Terreno', 'Casa', 'Apartamento', 'Gleba', 'Área rural', 'Fazenda', 'Lote',
    'Comercial', 'Galpão comercial', 'Galpão industrial', 'Galpão logístico',
    'Casa em condomínio', 'Conjunto comercial', 'Laje comercial', 'Hospital',
    'Prédio Residencial', 'Gleba de terras', 'Sítio', 'Chácara', 'Hotel',
    'Ilha', 'Resort', 'Prédio comercial', 'Imóvel industrial'
  ];

  private compareStrings(a: string, b: string): number {
    // localeCompare with sensitivity 'base' ignores accents/diacritics
    return a.localeCompare(b, 'pt', {sensitivity: 'base'});
  }

  private async ensureStatesLoaded(): Promise<void> {
    if (this.statesToCitiesCache) {
      return;
    }
    if (this.statesLoadPromise) {
      return this.statesLoadPromise;
    }
    this.statesLoadPromise = (async () => {
      // Cache mapping as received
      this.statesToCitiesCache = await firstValueFrom(
        this.httpClient.get<{ [key: string]: { 'city': string, 'file': string }[] }>(
          `${this.environmentConfig.apiUrl}/real-estates/states`,
          {'headers': this.headers}
        )
      );
    })().catch(error => {
      this.statesLoadPromise = null;
      throw error;
    });
    return this.statesLoadPromise;
  }

  // getRealEstates() {
  //   return this.httpClient.get<RealEstate[]>(`${this.environmentConfig.apiUrl}/real-estates`, {'headers': this.headers})
  // }

  // Return the list of real estate types
  getRealEstateTypes(): Observable<string[]> {
    return of(this.REAL_ESTATE_TYPES);
  }

  async getStates(): Promise<string[]> {
    await this.ensureStatesLoaded();
    const states = Object.keys(this.statesToCitiesCache || {});
    // sort ignoring accents
    states.sort(this.compareStrings.bind(this));
    return states;
  }

  async getCities(state: string): Promise<{ city: string; file: string }[]> {
    await this.ensureStatesLoaded();
    const cityObjs: {
      city: string;
      file: string
    }[] = (this.statesToCitiesCache && this.statesToCitiesCache[state]) ? this.statesToCitiesCache[state] : [];
    // Return objects sorted by city name ignoring accents/diacritics
    const sorted = [...cityObjs].sort((a, b) => this.compareStrings(a.city, b.city));
    return sorted;
  }

  /**
   * Fetch properties by city file token
   */
  getRealEstatesByCity(state: string, city: string, includeAuth = true): Observable<RealEstateCards[]> {

    return new Observable<RealEstateCards[]>((observer) => {
      const fetchRealEstates = (headers: HttpHeaders) => {
        this.httpClient.get<RealEstateCards[]>(`${this.environmentConfig.apiUrl}/real-estates/states/${state}/cities/${city}`, {headers})
          .subscribe({
            next: (data) => {
              observer.next(data);
              observer.complete();
            },
            error: (error) => {
              observer.error(error);
              observer.complete();
            }
          });
      };

      if (!includeAuth) {
        fetchRealEstates(this.headers);
        return;
      }

      this.authenticationService.getIdToken().then(idToken => {
        const headers = typeof idToken !== 'undefined'
          ? this.headers.set('Authorization', idToken)
          : this.headers;

        fetchRealEstates(headers);
      }).catch(err => {
        observer.error(err);
        observer.complete();
      });
    });
  }

  getRealEstate(id: string, bypassCache: boolean = false): Observable<RealEstate> {
    // Return from cache if available and not bypassing
    if (!bypassCache) {
      const cached = this.realEstateCache.get(id);
      if (cached) {
        return of(cached);
      }
    }

    return new Observable<RealEstate>((observer) => {
      console.log(`Fetching real estate ${id} from API`);
      this.authenticationService.getIdToken().then(idToken => {
        const headers = typeof idToken !== 'undefined'
          ? this.headers.set('Authorization', idToken)
          : this.headers;

        this.httpClient.get<RealEstate>(`${this.environmentConfig.apiUrl}/real-estates/${id}`, {headers}).subscribe({
          next: (data) => {
            // Cache and emit
            this.realEstateCache.set(id, data);
            observer.next(data);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        })
      }).catch(err => {
        observer.error(err);
        observer.complete();
      })
    })
  }

  getExampleRealEstates(): Observable<RealEstate[]> {
    return this.httpClient.get<RealEstate[]>(`${this.environmentConfig.apiUrl}//real-estates/examples`, {'headers': this.headers})
  }

}
